'use client';

import { HStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  Edit03Icon,
  PlayIcon,
  SecurityCheckIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Button } from '../../../../../components/button';
import { IconButton } from '../../../../../components/icon-button';
import {
  MenuWithItems,
  type MenuItemProps,
} from '../../../../../components/styled/menu';
import { Tooltip } from '../../../../../components/tooltip';
import { BodyDefault, MonoSmall } from '../../../../../components/typography';
import type { PendingApprovalControls } from '../../tool-config-types';
import {
  getHitlApprovalShortcutLabels,
  useHitlApprovalShortcuts,
} from '../hooks/useHitlApprovalShortcuts';

export function HitlApprovalFooter({
  onApprove,
  onReject,
  onEdit,
  onAlwaysAllow,
  alwaysAllowPattern,
  disabled,
  enableShortcuts,
}: PendingApprovalControls) {
  const t = useTranslations(
    'designSystem.chat.hitlRequestConfirmationPromptCard',
  );
  const tCommon = useTranslations('designSystem.common');
  const shortcuts = getHitlApprovalShortcutLabels();

  useHitlApprovalShortcuts({
    enabled: Boolean(enableShortcuts) && !disabled,
    onApprove,
    onReject,
  });

  const runOptions: MenuItemProps[] = [
    ...(onAlwaysAllow && alwaysAllowPattern
      ? [
          {
            value: 'always-allow',
            labelTooltip: t('alwaysAllowTooltip', {
              pattern: alwaysAllowPattern,
            }),
            onClick: onAlwaysAllow,
            label: (
              <HStack gap={1.5} alignItems="center" minW={0}>
                <SecurityCheckIcon size={14} />
                <BodyDefault>{t('alwaysAllow')}</BodyDefault>
                <MonoSmall color="fg.muted" truncate>
                  {alwaysAllowPattern}
                </MonoSmall>
              </HStack>
            ),
          },
        ]
      : []),
    ...(onEdit
      ? [
          {
            value: 'edit',
            onClick: onEdit,
            label: (
              <HStack gap={1.5} alignItems="center">
                <Edit03Icon size={14} />
                <BodyDefault>{t('editCommand')}</BodyDefault>
              </HStack>
            ),
          },
        ]
      : []),
  ];
  const hasRunOptions = runOptions.length > 0;

  return (
    <HStack
      justify="flex-end"
      gap={1}
      px={2}
      py={1.5}
      borderTopWidth="1px"
      borderTopColor="border.neutral.muted"
      onClick={(event) => event.stopPropagation()}
    >
      <Tooltip
        title={t('shortcutHint', {
          action: tCommon('reject'),
          shortcut: shortcuts.reject,
        })}
        openDelay={300}
      >
        <Button
          variant="ghost"
          size="2xs"
          aria-label={tCommon('reject')}
          onClick={onReject}
          disabled={disabled}
        >
          {tCommon('reject')}
        </Button>
      </Tooltip>
      <HStack gap={0}>
        <Tooltip
          title={t('shortcutHint', {
            action: t('run'),
            shortcut: shortcuts.approve,
          })}
          openDelay={300}
        >
          <Button
            variant="outline"
            size="2xs"
            aria-label={t('run')}
            onClick={onApprove}
            disabled={disabled}
            borderRightRadius={hasRunOptions ? '0' : undefined}
          >
            <PlayIcon size={12} />
            {t('run')}
          </Button>
        </Tooltip>
        {hasRunOptions ? (
          <MenuWithItems
            items={runOptions}
            contentWidth="260px"
            portalled
            positioning={{ strategy: 'fixed', placement: 'bottom-end' }}
            trigger={
              <IconButton
                variant="outline"
                size="2xs"
                aria-label={t('moreRunOptions')}
                disabled={disabled}
                borderLeftRadius="0"
                borderLeftWidth="0"
              >
                <ArrowDown01Icon size={12} />
              </IconButton>
            }
          />
        ) : null}
      </HStack>
    </HStack>
  );
}
